import { getErrorMessage } from '../../../shared/errors/getErrorMessage';

// Extraído de `ComponentTaskHoy.jsx` (no contemplado en las fases A-E originales del plan) --
// los 3 verbos de persistencia de tareas (borrar/crear/actualizar) que el resto del container
// (y varios de los hooks ya extraídos: `useTaskGanttInteraction`, `useTaskCellPopovers`) reciben
// como `updateTaskInDB`.
export function useTaskCrud({
    deleteTaskMutation, createTaskMutation, updateTaskMutation,
    toast, translate, navigate, activeSpaceId, activeTab, todayStr, user, basePath,
    setDeletingTaskId, setActiveTaskDropdownId,
}) {
    // Ejemplo trabajado del sistema
    // de errores nuevo — antes esta función no mostraba ningún error al usuario si el borrado
    // fallaba (ni siquiera revisaba res.ok), solo lo dejaba en el estado "borrando" para
    // siempre. `apiClient.del` lanza ApiError con el `code` del backend; `getErrorMessage`
    // lo mapea al texto para el usuario (ej. code 3004: "Solo quien creó esta tarea o un
    // administrador pueden eliminarla.").
    const handleDeleteTask = async (taskId) => {
        try {
            // `useDeleteTask` invalida `['tasks','list']`/`['tasks','standalone']` en `onSuccess`;
            // el socket `task_deleted` lo refuerza.
            await deleteTaskMutation.mutateAsync(taskId);
        } catch (e) {
            console.error("Error deleting task", e);
            toast.error(getErrorMessage(e));
        }
        setDeletingTaskId(null);
        setActiveTaskDropdownId(null);
    };

    const handleCreateNewTask = async (overrideSpace = null, type = 'task') => {
        try {
            const dateStr = todayStr;
            const targetSpaceId = overrideSpace ? overrideSpace.id : (activeSpaceId || null);

            if (!targetSpaceId) {
                // Rule: Tasks cannot be created without a space.
                // The UI should prevent this, but just in case:
                toast.error("Selecciona un espacio primero.");
                return;
            }

            const data = await createTaskMutation.mutateAsync({
                // Título vacío a propósito -- el input muestra "Nueva Tarea" como placeholder
                // (modal.titlePlaceholder), no como texto real precargado que hay que borrar
                // a mano. Ver taskValidators.js (validateCreateTask): el título ya no exige
                // notEmpty() para poder crear con este valor.
                title: '',
                description: '',
                type,
                status: 'open',
                stage: 'planeada',
                date: dateStr,
                start_time: null,
                end_time: null,
                priority: null,
                assignee: null,
                supervisor: null,
                actorId: user ? user.id : null,
                space_id: targetSpaceId,
                ...(activeTab === 'flow' && window.currentRoadmapCenter ? {
                    roadmap_x: window.currentRoadmapCenter.x,
                    roadmap_y: window.currentRoadmapCenter.y
                } : {})
            });
            if (data.success && data.task) {
                // el socket `task_created` + el `onSuccess` de `useCreateTask` invalidan la lista
                if (overrideSpace) {
                    navigate(`/${encodeURIComponent(overrideSpace.name.toLowerCase())}/task/list/today/${data.task.id}`);
                } else {
                    navigate(`${basePath}/${data.task.id}`);
                }
            }
        } catch (err) {
            console.error("Error creating new task:", err);
            toast.error(translate('errors.createTaskFailed'));
        }
    };

    // Call REST API to update task stage/status
    const updateTaskInDB = async (taskId, fields) => {
        try {
            const bodyFields = { ...fields };
            if (user?.id) {
                bodyFields.actorId = user.id;
            }
            const data = await updateTaskMutation.mutateAsync({ id: taskId, patch: bodyFields });
            return { success: true, data };
        } catch (error) {
            console.error("Failed to update task in DB:", error);
            // `UpdateTask.execute` manda mensajes específicos para casos como NO_CALENDAR/
            // NO_SLOTS en `body.message` (sin `code`, así que `getErrorMessage` por sí solo
            // caería al genérico) — se prioriza igual que hacía el `fetch()` original.
            toast.error(error?.body?.message || error?.body?.error || getErrorMessage(error));
            return { success: false };
        }
    };

    // Crear una "actualización" desde la bitácora de un Cuadro de Trabajo (integración
    // main→dev, epic D1.4): una tarea normal con `workframe_id` + `doc_id`, que se abre al
    // instante. Devuelve la tarea creada para que el panel la inserte en la bitácora.
    const createWorkframeUpdate = async ({ workframeId, docId, spaceId }) => {
        const targetSpaceId = spaceId || activeSpaceId || null;
        if (!targetSpaceId) {
            toast.error("Selecciona un espacio primero.");
            return null;
        }
        try {
            const data = await createTaskMutation.mutateAsync({
                title: '',
                description: '',
                type: 'task',
                status: 'open',
                stage: 'planeada',
                date: todayStr,
                priority: null,
                assignee: null,
                supervisor: null,
                actorId: user ? user.id : null,
                space_id: targetSpaceId,
                workframe_id: workframeId || null,
                doc_id: docId || null,
            });
            if (data.success && data.task) {
                navigate(`${basePath}/${data.task.id}`);
                return data.task;
            }
            return null;
        } catch (err) {
            console.error("Error creating workframe update:", err);
            toast.error(translate('errors.createTaskFailed'));
            return null;
        }
    };

    return { handleDeleteTask, handleCreateNewTask, updateTaskInDB, createWorkframeUpdate };
}
